import React from "react";
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaGlobe,
  FaYoutube,
  FaInstagram,
  FaFacebookF,
  FaWhatsapp,
} from "react-icons/fa";

const Footer = () => {
  const contactItems = [
    {
      icon: <FaPhone aria-hidden="true" focusable="false" />,
      label: "+91 9087917905",
      href: "tel:+919087917905",
      itemProp: "telephone",
    },
    {
      icon: <FaEnvelope aria-hidden="true" focusable="false" />,
      label: "info@stvmhss.in",
      href: "mailto:info@stvmhss.in",
      itemProp: "email",
    },
    {
      icon: <FaMapMarkerAlt aria-hidden="true" focusable="false" />,
      label: "No.10 Dhanalakshmi Nagar, Vanagaram, Chennai - 600095",
      // address is handled separately with <address> microdata
    },
    {
      icon: <FaGlobe aria-hidden="true" focusable="false" />,
      label: "www.stvmhss.in",
      href: "https://www.stvmhss.in",
      itemProp: "url",
    },
  ];

  const socialLinks = [
    {
      icon: <FaYoutube aria-hidden="true" focusable="false" />,
      href: "https://www.youtube.com/@stthomasvidyalayamvioleted7279",
      color: "hover:text-red-500",
      label: "YouTube",
    },
    {
      icon: <FaInstagram aria-hidden="true" focusable="false" />,
      href: "https://www.instagram.com/_stvmhss_/",
      color: "hover:text-pink-400",
      label: "Instagram",
    },
    {
      icon: <FaFacebookF aria-hidden="true" focusable="false" />,
      href: "https://www.facebook.com/share/1PeJ6p1fD7/",
      color: "hover:text-blue-400",
      label: "Facebook",
    },
    {
      icon: <FaWhatsapp aria-hidden="true" focusable="false" />,
      href: "https://www.whatsapp.com/channel/0029Va9R63NAojZ17JrlGS35",
      color: "hover:text-green-400",
      label: "WhatsApp",
    },
  ];

  return (
    <footer
      className="w-full 
             py-16 md:py-20 lg:py-24 
             bg-black text-[#F5F5DC] 
             px-4 sm:px-6 lg:px-12 font-sans"
      role="contentinfo"
      aria-label="Site footer"
      itemScope
      itemType="https://schema.org/School"
    >
      <meta itemProp="name" content="St. Thomas Vidyalayam" />
      <link itemProp="url" href="https://www.stvmhs.in" />
      <div className="max-w-7xl mx-auto">
        {/* Top Section */}
        <div className="flex flex-col lg:flex-row flex-wrap items-center lg:items-start justify-between gap-10">
          {/* Logo */}
          <div className="flex flex-col items-center lg:items-start gap-4 flex-shrink-0 w-full lg:w-auto">
            <img
              src="https://res.cloudinary.com/ddvpczzng/image/upload/v1757446087/emblem_cqwlrr.png"
              alt="STVMHS Emblem"
              loading="lazy"
              decoding="async"
              width="128"
              height="128"
              className="w-20 sm:w-24 md:w-28 lg:w-32 object-contain transition-transform duration-300 hover:scale-105"
              onError={(e) => {
                const target = e.target;
                if (target && target.style) target.style.display = "none";
              }}
            />
            <div className="text-center lg:text-left">
              <h2
                className="text-xl md:text-2xl font-bold"
                itemProp="alternateName"
              >
                St. Thomas Vidyalayam
              </h2>
              <p className="text-sm md:text-base opacity-80">
                Matric | Higher Secondary School
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="hidden lg:block h-auto min-h-[180px] w-px bg-[#F5F5DC]/30"></div>

          {/* Contact Info */}
          <div className="space-y-4 w-full lg:w-auto">
            {contactItems.map(({ icon, label, href, itemProp }, i) => (
              <div
                key={i}
                className="flex items-start justify-center lg:justify-start gap-3"
              >
                <span className="text-base mt-0.5 flex-shrink-0">{icon}</span>
                {href ? (
                  <a
                    href={href}
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel={
                      href.startsWith("http")
                        ? "noopener noreferrer"
                        : undefined
                    }
                    className="hover:underline hover:opacity-80 text-sm sm:text-base text-center lg:text-left"
                    itemProp={itemProp}
                    aria-label={label}
                  >
                    {label}
                  </a>
                ) : (
                  // Wrap address in semantic element with microdata
                  <address
                    className="not-italic text-sm sm:text-base text-center lg:text-left"
                    itemProp="address"
                    itemScope
                    itemType="https://schema.org/PostalAddress"
                  >
                    <span itemProp="streetAddress">
                      No.10 Dhanalakshmi Nagar, Vanagaram
                    </span>
                    , <span itemProp="addressLocality">Chennai</span> -{" "}
                    <span itemProp="postalCode">600095</span>
                    <meta itemProp="addressCountry" content="IN" />
                    <meta itemProp="addressRegion" content="TN" />
                  </address>
                )}
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="hidden lg:block h-auto min-h-[180px] w-px bg-[#F5F5DC]/30"></div>

          {/* Social & Map */}
          <div className="flex flex-col items-center gap-6 w-full lg:w-auto">
            {/* Social Links */}
            <div
              className="flex gap-5 text-xl md:text-2xl"
              aria-label="Social links"
            >
              {socialLinks.map(({ icon, href, color, label }, i) => (
                <a
                  key={i}
                  href={href}
                  aria-label={label}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-[#F5F5DC] ${color} transition-colors duration-300`}
                >
                  {icon}
                </a>
              ))}
            </div>

            {/* Map */}
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3886.6514183840427!2d80.15025687578257!3d13.05784618726547!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a52617261b0aa17%3A0xef80f22da9e567a3!2sSt%20Thomas%20Vidyalaya!5e0!3m2!1sen!2sin!4v1745074970522!5m2!1sen!2sin"
              title="St. Thomas Vidyalayam Location"
              aria-label="Map to St. Thomas Vidyalayam"
              className="w-full sm:w-[350px] h-48 sm:h-52 border border-[#F5F5DC] opacity-80 hover:opacity-100 transition-opacity duration-300 rounded-lg"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-10 pt-6 border-t border-[#F5F5DC]/20 text-center text-[#F5F5DC]/70 text-xs sm:text-sm">
          © {new Date().getFullYear()}{" "}
          <span itemProp="name">St. Thomas Vidyalayam</span>. All rights
          reserved.
          <span className="block mt-1">
            Created by{" "}
            <a
              href="https://varshavijayabaskar.netlify.app"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#F5F5DC] hover:underline hover:text-white transition"
            >
              Varsha
            </a>{" "}
            <span className="opacity-80">Alumni @ STVMHSS</span>
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
